import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { PlasticRoute } from '../../types';

/** 聚氯乙烯(PVC)电石乙炔法 P&ID 详细图：电石水解发生乙炔 → 净化 → 与HCl合成VCM → 悬浮聚合 → 汽提干燥 */
const media = {
  carbide: { label: '电石(CaC₂)', light: '#4e342e', dark: '#a1887f', width: 2.6 },
  water: { label: '水', light: '#1565c0', dark: '#64b5f6', width: 2 },
  acetylene: { label: '乙炔(C₂H₂)', light: '#4527a0', dark: '#b39ddb', width: 2.6 },
  hcl: { label: '氯化氢(HCl)', light: '#c2185b', dark: '#f06292', width: 2 },
  vcm: { label: '氯乙烯单体(VCM)', light: '#5c6bc0', dark: '#9fa8da', width: 2.6 },
  pvc: { label: 'PVC浆料/粉料', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  slurry: { label: '电石渣浆', light: '#616161', dark: '#b0bec5', width: 2 },
};

const devices = [
  { id: 'v101', kind: 'vDrum' as const, x: 280, y: 161, tag: 'V-101', name: '电石储仓' },
  { id: 'r101', kind: 'digester' as const, x: 400, y: 116, tag: 'R-101', name: '乙炔发生器', note: 'CaC₂+2H₂O→C₂H₂' },
  { id: 't101', kind: 'column' as const, x: 550, y: 94, tag: 'T-101', name: '乙炔净化塔', note: '脱硫脱磷' },
  { id: 'r102', kind: 'reactor' as const, x: 730, y: 128, tag: 'R-102', name: '氯乙烯转化器', note: 'C₂H₂+HCl→VCM' },
  { id: 't102', kind: 'column' as const, x: 880, y: 94, tag: 'T-102', name: 'VCM精制塔', note: '脱水脱残HCl' },
  { id: 'r103', kind: 'reactor' as const, x: 1030, y: 128, tag: 'R-103', name: '聚合釜', note: '50~70℃悬浮聚合' },
  { id: 't103', kind: 'column' as const, x: 1180, y: 101, tag: 'T-103', name: '汽提塔' },
  { id: 'v102', kind: 'vDrum' as const, x: 1320, y: 161, tag: 'V-102', name: '干燥器' },
];

const ports: PidPort[] = [
  { id: 'p-carbide', label: '电石(见电石工业路线)', medium: 'carbide', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-water', label: '水', medium: 'water', x: 345, y: 40, direction: 'in' as const },
  { id: 'p-hcl', label: '氯化氢(HCl)', medium: 'hcl', x: 636, y: 40, direction: 'in' as const },
  { id: 'p-pvc', label: '聚氯乙烯(PVC)', medium: 'pvc', x: 1450, y: 224, direction: 'out' as const },
  { id: 'p-slurry', label: '电石渣浆(制水泥)', medium: 'slurry', x: 495, y: 620, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-carbide', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'carbide' },
  { id: 's02', from: 'v101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'carbide' },
  { id: 's03', from: 'p-water', fromSide: 'right', to: 'r101', toSide: 'top', medium: 'water' },
  { id: 's04', from: 'r101', fromSide: 'right', to: 't101', toSide: 'left', medium: 'acetylene' },
  { id: 's05', from: 't101', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'acetylene' },
  { id: 's06', from: 'p-hcl', fromSide: 'right', to: 'r102', toSide: 'top', medium: 'hcl' },
  { id: 's07', from: 'r102', fromSide: 'right', to: 't102', toSide: 'left', medium: 'vcm' },
  { id: 's08', from: 't102', fromSide: 'right', to: 'r103', toSide: 'left', medium: 'vcm' },
  { id: 's09', from: 'r103', fromSide: 'right', to: 't103', toSide: 'left', medium: 'pvc' },
  { id: 's10', from: 't103', fromSide: 'right', to: 'v102', toSide: 'left', medium: 'pvc' },
  { id: 's11', from: 'v102', fromSide: 'right', to: 'p-pvc', toSide: 'left', medium: 'pvc' },
  { id: 's12', from: 'r101', fromSide: 'bottom', to: 'p-slurry', toSide: 'left', medium: 'slurry' },
];

const PvcCarbideDiagram: React.FC<{ route: PlasticRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="电石乙炔法PVC装置详细流程"
    subtitle="电石(碳化钙)湿式水解发生乙炔(C₂H₂) → 净化脱硫脱磷 → 与氯化氢(HCl)合成氯乙烯单体(VCM) → 精制 → 悬浮聚合 → 汽提 → 干燥，电石渣浆综合利用（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default PvcCarbideDiagram;
